import { STORAGE_KEYS } from '../constants';
import type { IPersonalizationStorageAdapter } from '../types';

/**
 * Storage adapter for web browsers using localStorage and sessionStorage.
 *
 * Storage strategy:
 * - `actorId`: persisted in localStorage (survives browser close)
 * - `sessionId`: stored in sessionStorage (cleared on tab close)
 * - `sessionTimestamp`: stored in sessionStorage (cleared on tab close)
 *
 * Handles Safari private mode gracefully by catching storage exceptions.
 * Returns null for all getters when running in non-browser environments (SSR).
 *
 * @class WebPersonalizationStorageAdapter
 * @implements {IPersonalizationStorageAdapter}
 *
 * @example
 * const storage = new WebPersonalizationStorageAdapter();
 * const client = new PersonalizationClient({
 *   storage,
 *   // ... other config
 * });
 */
export class WebPersonalizationStorageAdapter implements IPersonalizationStorageAdapter {
  getActorId(): string | null {
    return this.getLocalStorageItem(STORAGE_KEYS.ACTOR_ID);
  }

  setActorId(actorId: string): void {
    this.setLocalStorageItem(STORAGE_KEYS.ACTOR_ID, actorId);
  }

  removeActorId(): void {
    this.removeLocalStorageItem(STORAGE_KEYS.ACTOR_ID);
  }

  getSessionId(): string | null {
    return this.getSessionStorageItem(STORAGE_KEYS.SESSION_ID);
  }

  setSessionId(sessionId: string): void {
    this.setSessionStorageItem(STORAGE_KEYS.SESSION_ID, sessionId);
  }

  removeSessionId(): void {
    this.removeSessionStorageItem(STORAGE_KEYS.SESSION_ID);
  }

  getSessionTimestamp(): number | null {
    const raw = this.getSessionStorageItem(STORAGE_KEYS.SESSION_TS);
    if (raw === null) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }

  setSessionTimestamp(timestamp: number): void {
    this.setSessionStorageItem(STORAGE_KEYS.SESSION_TS, String(timestamp));
  }

  removeSessionTimestamp(): void {
    this.removeSessionStorageItem(STORAGE_KEYS.SESSION_TS);
  }

  private getLocalStorageItem(key: string): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private setLocalStorageItem(key: string, value: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch {
      // Safari private mode may throw
    }
  }

  private removeLocalStorageItem(key: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch {
      // Safari private mode may throw
    }
  }

  private getSessionStorageItem(key: string): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private setSessionStorageItem(key: string, value: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Safari private mode may throw
    }
  }

  private removeSessionStorageItem(key: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Safari private mode may throw
    }
  }
}
